import qs from "query-string";
import { FC } from "react";
import { useFormContext } from "react-hook-form";
import { Link } from "react-router-dom";
import { createEnumParam, encodeQueryParams } from "serialize-query-params";
import styled from "styled-components";
import { ObjectParam } from "use-query-params";

import { useDynamicFormsTranslate } from "@ryde/redux";
import {
  BookingsRoutes,
  DATA_E2E_KEY,
  ICustomField,
  IStyledAttributes,
  Log,
  NO_VALUE_DISPLAY,
  RideRoutes,
  RydeAppUnit,
  serializeQsFilterModel,
  TOP_CALENDAR_MODE_QUERY_NAME,
  TopCalendarMode,
  usePrefixedRouting,
} from "@ryde/shared";
import { blueMui } from "@ryde/theme";

export const LinkToAnotherEntityStyled = styled(Link).attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "link-to-another-entity",
})`
  color: ${blueMui.A400};
  text-decoration: underline;
  cursor: pointer;
`;

const entityGidPathMappings: Partial<Record<RydeAppUnit, string>> = {
  booking: BookingsRoutes.SEARCH_CALENDAR,
  ride: RideRoutes.SEARCH_CARD_BOARD,
};

const resolvePathByEntity = (entity: RydeAppUnit): string | null => {
  if (entity in entityGidPathMappings) {
    return entityGidPathMappings[entity] ?? null;
  }

  return null;
};

export const LinkToAnotherEntityGridFilteredByCurrentTemplate: FC<
  ICustomField
> = ({ name, formAttributes, formLabel }) => {
  const { getValues } = useFormContext();
  const { formLabelTranslated } = useDynamicFormsTranslate({
    name,
    fallbackText: formLabel,
  });

  const linkToAnotherEntityGridFilteredByCurrentParams =
    formAttributes?.linkToAnotherEntityGridFilteredByCurrentParams;
  const { addPrefix } = usePrefixedRouting();
  const id = getValues("id");

  const serializedFilters = serializeQsFilterModel({
    [linkToAnotherEntityGridFilteredByCurrentParams?.filterFieldId ?? 0]: [id],
  });

  if (!linkToAnotherEntityGridFilteredByCurrentParams?.entityName) {
    return NO_VALUE_DISPLAY;
  }

  const pathByEntity = resolvePathByEntity(
    linkToAnotherEntityGridFilteredByCurrentParams?.entityName,
  );

  if (!pathByEntity) {
    return NO_VALUE_DISPLAY;
  }

  if (!linkToAnotherEntityGridFilteredByCurrentParams?.calendarMode) {
    Log.warn("Calendar mode for link didn't provided used today mode");
  }

  const encodedQuery = encodeQueryParams(
    {
      filters: ObjectParam,
      [TOP_CALENDAR_MODE_QUERY_NAME]: createEnumParam<TopCalendarMode>(
        Object.values(TopCalendarMode),
      ),
    },
    {
      filters: serializedFilters,
      [TOP_CALENDAR_MODE_QUERY_NAME]:
        linkToAnotherEntityGridFilteredByCurrentParams?.calendarMode ??
        TopCalendarMode.TODAY,
    },
  );

  const linkPath = addPrefix(`${pathByEntity}?${qs.stringify(encodedQuery)}`);

  return (
    <LinkToAnotherEntityStyled
      rel="noopener noreferrer"
      to={`${linkPath}`}
      target="_blank"
    >
      {formLabelTranslated}
    </LinkToAnotherEntityStyled>
  );
};
